import type * as ents from '@attain-apps/entities'

/**
 * Per-key in-flight or last-failed status of a load/save operation.
 *
 * Mutually exclusive — at any time the entry is either:
 *  - idle (`status` undefined),
 *  - loading from the backend,
 *  - submitting a save to the backend, or
 *  - in an error state from the most recent attempt.
 */
export type Status = ExactlyOneOf<{
  isLoading: true
  isSubmitting: true
  error: Error
}>

/**
 * Cached state for a single configuration key (per-agent default or
 * per-conversation override).
 */
export interface Entry {
  /**
   * Last known persisted configuration. Stays populated across
   * re-loads and during in-flight saves so the UI never blanks out.
   */
  readonly value?: ents.core.agent.Configuration
  /** {@link Status} */
  readonly status?: Status
}

/**
 * Active editing session for the configuration modal.
 *
 * Presence of this field doubles as "the modal is open" — a selector
 * derived from it drives the UI, so opening/closing the modal no
 * longer requires per-component flags or event emitters.
 *
 * The `draft*` fields are wrapped (rather than bare `Context | undefined`)
 * so we can distinguish "user hasn't touched this scope yet — fall back
 * to the persisted value" from "user explicitly set this scope to
 * undefined / inherit".
 */
export interface EditingSession {
  readonly agentId: ents.core.agent.Agent.Id
  readonly conversationId?: ents.core.agent.Conversation.Id
  /** Explicit user edits to the agent-default draft, if any. */
  readonly draftAgent?: {
    readonly context: ents.core.agent.Configuration.Context | undefined
  }
  /** Explicit user edits to the per-conversation draft, if any. */
  readonly draftConversation?: {
    readonly context: ents.core.agent.Configuration.Context | undefined
  }
}

/**
 * State for the agent configuration feature.
 *
 * Two independent lookups, both keyed by the natural identifier:
 *  - {@link byAgent} — agent-default contexts, keyed by agent id
 *  - {@link byConversation} — per-conversation overrides, keyed by
 *    conversation id (each conversation belongs to exactly one agent so
 *    the conversation id alone uniquely identifies the override).
 *
 * Plus an optional {@link editing} field describing the in-progress
 * configuration edit (if any).
 */
export interface State {
  readonly byAgent: Readonly<Partial<Record<ents.core.agent.Agent.Id, Entry>>>
  readonly byConversation: Readonly<Partial<Record<string, Entry>>>
  readonly availableKnowledgeBaseFilesByAgent: Readonly<
    Partial<
      Record<
        ents.core.agent.Agent.Id,
        readonly ents.core.agent.KnowledgeBase.File[]
      >
    >
  >
  readonly editing?: EditingSession
}
export namespace State {
  export const INITIAL: Readonly<State> = {
    availableKnowledgeBaseFilesByAgent: {},
    byAgent: {},
    byConversation: {},
  }

  /**
   * Fluent builder for producing new {@link State} values inside reducers.
   *
   * Every method returns a new `Helper` (immutable), and because `Helper`
   * implements `State` you can return it directly wherever `State` is
   * expected — no terminal `.state` call needed.
   *
   * ```ts
   * State.helper(state)
   *   .setAgentStatus(agentId, { isLoading: true })
   *   .setConversationStatus(conversationId, { isLoading: true })
   * ```
   */
  export class Helper implements State {
    public readonly byAgent: State['byAgent']
    public readonly byConversation: State['byConversation']
    public readonly availableKnowledgeBaseFilesByAgent: State['availableKnowledgeBaseFilesByAgent']
    public readonly editing: State['editing']

    public constructor(state: State) {
      this.availableKnowledgeBaseFilesByAgent =
        state.availableKnowledgeBaseFilesByAgent
      this.byAgent = state.byAgent
      this.byConversation = state.byConversation
      this.editing = state.editing
    }

    /** Sets the status of an agent-default entry (preserves its value). */
    public setAgentStatus(
      agentId: ents.core.agent.Agent.Id,
      status: Entry['status'],
    ): Helper {
      return new Helper({
        ...this,
        byAgent: {
          ...this.byAgent,
          [agentId]: { ...this.byAgent[agentId], status },
        },
      })
    }

    /** Sets the persisted value of an agent-default entry and clears status. */
    public setAgentValue(
      agentId: ents.core.agent.Agent.Id,
      value: Entry['value'],
    ): Helper {
      return new Helper({
        ...this,
        byAgent: {
          ...this.byAgent,
          [agentId]: { ...this.byAgent[agentId], status: undefined, value },
        },
      })
    }

    /** Sets the available knowledge-base files for an agent. */
    public setAgentAvailableKnowledgeBaseFiles(
      agentId: ents.core.agent.Agent.Id,
      files: readonly ents.core.agent.KnowledgeBase.File[],
    ): Helper {
      return new Helper({
        ...this,
        availableKnowledgeBaseFilesByAgent: {
          ...this.availableKnowledgeBaseFilesByAgent,
          [agentId]: files,
        },
      })
    }

    /**
     * Sets the status of a per-conversation entry (preserves its value).
     * No-op when `conversationId` is `undefined`.
     */
    public setConversationStatus(
      conversationId: ents.core.agent.Conversation.Id | undefined,
      status: Entry['status'],
    ): Helper {
      if (!conversationId) {
        return this
      }
      return new Helper({
        ...this,
        byConversation: {
          ...this.byConversation,
          [conversationId]: { ...this.byConversation[conversationId], status },
        },
      })
    }

    /**
     * Sets the persisted value of a per-conversation entry and clears status.
     * No-op when `conversationId` is `undefined`.
     */
    public setConversationValue(
      conversationId: ents.core.agent.Conversation.Id | undefined,
      value: Entry['value'],
    ): Helper {
      if (!conversationId) {
        return this
      }
      return new Helper({
        ...this,
        byConversation: {
          ...this.byConversation,
          [conversationId]: {
            ...this.byConversation[conversationId],
            status: undefined,
            value,
          },
        },
      })
    }

    /** Removes a per-conversation entry entirely from the cache. */
    public deleteConversation(
      conversationId: ents.core.agent.Conversation.Id,
    ): Helper {
      const next = { ...this.byConversation }
      delete next[conversationId]
      return new Helper({ ...this, byConversation: next })
    }

    /** Opens (or replaces) the editing session. */
    public setEditing(session: EditingSession): Helper {
      return new Helper({ ...this, editing: session })
    }

    /** Closes the editing session. */
    public clearEditing(): Helper {
      return new Helper({ ...this, editing: undefined })
    }

    /** Updates the agent-default draft within the current editing session. */
    public setAgentDraft(
      context: ents.core.agent.Configuration.Context | undefined,
    ): Helper {
      if (!this.editing) {
        return this
      }
      return new Helper({
        ...this,
        editing: { ...this.editing, draftAgent: { context } },
      })
    }

    /** Updates the per-conversation draft within the current editing session. */
    public setConversationDraft(
      context: ents.core.agent.Configuration.Context | undefined,
    ): Helper {
      if (!this.editing) {
        return this
      }
      return new Helper({
        ...this,
        editing: { ...this.editing, draftConversation: { context } },
      })
    }
  }

  /** Creates a {@link Helper} seeded with `state`. */
  export function helper(state: State): Helper {
    return new Helper(state)
  }
}
